import type { UseQueryResult } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { GetMeResponse } from '@snootsvet/shared/portal'

import { AccountFetchError } from '@/customer/components/account-fetch-error'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { memberIsOnboarded } from '@/customer/lib/membership'

/**
 * Where a signed-in visitor sits relative to the onboarded-member
 * zone. Kept as a discriminated union so the component is a thin
 * renderer over a pure reducer (see `resolveOnboardedGateStatus`)
 * and the decision logic is unit-testable without React or a DOM.
 *
 * - `loading`    - `/me` hasn't resolved yet
 * - `error`      - `/me` failed; the outer zone shows a fallback
 * - `onboarding` - signed in but hasn't finished registration
 *                  (no verified email / no subscribed pet / etc.)
 * - `onboarded`  - fully set up, can enter the portal proper
 *
 * The `anon` case isn't modelled here: `<OnboardedGate>` sits behind
 * `<SignedIn>` in the route tree, so an unsigned visitor never
 * reaches the reducer.
 */
export type OnboardedGateStatus =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'onboarding' }
  | { kind: 'onboarded' }

type MeQueryResult = Pick<UseQueryResult<GetMeResponse>, 'isPending' | 'isError' | 'data'>

/**
 * Pure reducer. `error` is checked before `pending` because React
 * Query can briefly report both while transitioning; the error is
 * the more actionable signal.
 */
export function resolveOnboardedGateStatus(me: MeQueryResult): OnboardedGateStatus {
  if (me.isError) return { kind: 'error' }
  if (me.isPending || !me.data) return { kind: 'loading' }
  if (!memberIsOnboarded(me.data.member, me.data.patients)) return { kind: 'onboarding' }
  return { kind: 'onboarded' }
}

/**
 * Account-state resolution for the onboarded-member zone. Reads the
 * `/portal/me` snapshot and dispatches on the resolved status.
 */
export function OnboardedGate({ children }: { children: ReactNode }) {
  const me = useGetMe()
  const status = resolveOnboardedGateStatus(me)

  switch (status.kind) {
    case 'error':
      return <AccountFetchError />
    case 'loading':
      return null
    case 'onboarding':
    case 'onboarded':
      return children
  }
}
